import { DynamicPageContent } from "@pdfking/book3d"
import type { DestinationID, ImposeResult } from "@pdfking/imposition"
import { JobDestinationList } from "@pdfking/imposition"
import type { FileSlot } from "@pdfking/imposition-form"
import type { Distance, Margins } from "@pdfking/math"
import type { SinglePagePdf } from "@pdfking/pdfengine"
import type { PageNumber } from "@pdfking/renderer-api"
import { GroupMatrix } from "@pdfking/renderer-api"
import type { FileName } from "@pdfking/structs"
import type { ContentRenderer } from "@pdfking/templates"
import type { ContentRequest } from "@pdfking/templates-content"
import { ContentGUID } from "@pdfking/templates-content"
import type { StringGUID } from "@pdfking/tfunc"
import { Items } from "@pdfking/tfunc"

/** What draws a generated page: the bleed every page is drawn with and the
 * page a request is laid out to, in the background (see
 * `@pdfking/templates-content`'s CreateContentFile). */
export type GenerateContent = {
  bleed: Margins<Distance>
  file: (request: ContentRequest) => Promise<SinglePagePdf>
}

/** The slots the generated book fills, destination by destination: with no
 * upload attached every slot is the page of the generated book it is; a
 * coating's film always, uploads or not. Drawn in the print palette, or for an
 * overlay destination in the lamination one; seeded by `seed`, a book of its
 * own, every page drawn as `content` when one is given, else as its grow's
 * renderer, its months and weekdays named in `language`. Each slot names its
 * file by `filename`, one file per destination, its pages the destination's
 * in order. */
export const GeneratedSlots = ({
  content,
  filename,
  generate: { bleed, file },
  has_files,
  language,
  result,
  seed,
}: {
  content: ContentRenderer | undefined
  filename: (destination: DestinationID) => FileName
  generate: GenerateContent
  has_files: boolean
  language: string
  result: ImposeResult
  seed: StringGUID
}): Record<DestinationID, Record<PageNumber, FileSlot>> => {
  const book = result.book3d()
  const generated: Record<DestinationID, Record<PageNumber, FileSlot>> = {}
  for (const [destination, pages] of Items(result.destinations) as [
    DestinationID,
    PageNumber[],
  ][]) {
    const layer = JobDestinationList[destination]?.print_mode ?? "print"
    if (has_files && layer !== "coating") continue
    const slots: Record<PageNumber, FileSlot> = {}
    pages.forEach((page, i) => {
      const request = DynamicPageContent(
        book,
        seed,
        language,
        page,
        result.pages[page]!,
        layer,
        bleed
      )
      const page_number = (i + 1) as PageNumber
      const drawn = request && content ? { ...request, renderer: content } : request
      if (drawn)
        slots[page_number] = {
          filename: filename(destination),
          matrix: GroupMatrix(),
          // known from the request alone; laid out only when asked for
          page: {
            bleed: drawn.bleed,
            get_file: () => file(drawn),
            guid: ContentGUID(drawn),
            trim: drawn.size,
          },
          page_number,
        }
    })
    generated[destination] = slots
  }
  return generated
}
